import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { PaperPlaneTilt, LockKey } from "@phosphor-icons/react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { TwoFactorPanel } from "../components/TwoFactorPanel";

export default function LoginPage() {
  const { user, loginComplete } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [preToken, setPreToken] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.login(email, password);
      setPreToken(r.pre_token);
      setStage(r.stage);
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  };

  const isPreview = window.location.hostname.endsWith(".preview.emergentagent.com") || window.location.hostname === "localhost";
  const devLogin = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.devLogin();
      loginComplete(r.token, r.user);
      navigate("/", { replace: true });
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-lg bg-zinc-950 flex items-center justify-center">
            <PaperPlaneTilt size={20} weight="fill" className="text-white" />
          </div>
          <span className="font-heading text-xl font-semibold tracking-tight">MediHub</span>
        </div>

        <div className="rounded-xl border border-zinc-200 bg-white p-7 shadow-sm" data-testid="login-card">
          {stage === "credentials" && (
            <>
              <h1 className="font-heading text-lg font-medium mb-1">Sign in</h1>
              <p className="text-sm text-zinc-500 mb-5">Access is restricted to invited team members.</p>
              <form onSubmit={submit} className="space-y-3">
                <label className="block">
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Email</span>
                  <input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} autoFocus autoComplete="username"
                    className="block w-full h-10 rounded-md border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950 focus:border-zinc-950" />
                </label>
                <label className="block">
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Password</span>
                  <input data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password"
                    className="block w-full h-10 rounded-md border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950 focus:border-zinc-950" />
                </label>
                {err && <p data-testid="login-error" className="text-sm text-rose-600">{err}</p>}
                <button data-testid="login-submit" type="submit" disabled={busy || !email || !password}
                  className="w-full h-10 rounded-md bg-zinc-950 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 transition-colors">
                  {busy ? "Checking…" : "Continue"}
                </button>
              </form>
              {isPreview && (
                <>
                  <div className="flex items-center gap-2 my-4">
                    <div className="flex-1 h-px bg-zinc-200" />
                    <span className="text-[10px] uppercase tracking-wider text-zinc-400">preview only</span>
                    <div className="flex-1 h-px bg-zinc-200" />
                  </div>
                  <button data-testid="dev-login-btn" onClick={devLogin} disabled={busy}
                    className="w-full h-10 rounded-md border border-dashed border-zinc-300 text-sm text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 transition-colors">
                    Skip login (preview environment)
                  </button>
                  <p className="text-[11px] text-zinc-400 mt-2 text-center">Not available on the live site.</p>
                </>
              )}
            </>
          )}

          {(stage === "setup_2fa" || stage === "verify_2fa") && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <LockKey size={18} className="text-zinc-700" />
                <h1 className="font-heading text-lg font-medium">
                  {stage === "setup_2fa" ? "Set up two-factor authentication" : "Two-factor authentication"}
                </h1>
              </div>
              <p className="text-sm text-zinc-500 mb-5">
                {stage === "setup_2fa" ? "One-time setup — required for every account." : "Required on every sign-in."}
              </p>
              <TwoFactorPanel
                stage={stage}
                preToken={preToken}
                email={email}
                onSuccess={(token, u) => { loginComplete(token, u); navigate("/", { replace: true }); }}
              />
              <button data-testid="twofa-back" onClick={() => { setStage("credentials"); setPreToken(""); }}
                className="mt-4 text-xs text-zinc-500 hover:text-zinc-800">← Back to sign in</button>
            </>
          )}
        </div>

        <p className="text-center text-[11px] text-zinc-400 mt-5">
          MediHub · internal contact & campaign platform
        </p>
      </div>
    </div>
  );
}
